import { EditOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Drawer, Form } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  SelectOption,
  VendorErrorCodeDrawerMode,
  VendorErrorCodeFormValues,
} from '~/app/components/errorManagement/ErrorManagementPage/types';
import { Button } from '~/app/components/common/Button';
import { VendorErrorCodeAdvancedFields } from '~/app/components/errorManagement/VendorErrorCodeFormSections/VendorErrorCodeAdvancedFields';
import { VendorErrorCodeRequiredFields } from '~/app/components/errorManagement/VendorErrorCodeFormSections/VendorErrorCodeRequiredFields';
import { ViewDrawerTitle } from '~/app/components/errorManagement/ViewDrawerTitle/ViewDrawerTitle';

export interface VendorErrorCodeDrawerProps {
  open: boolean;
  mode: VendorErrorCodeDrawerMode;
  initialValues?: VendorErrorCodeFormValues;
  manufacturerOptions: SelectOption[];
  assetTypeOptions: SelectOption[];
  submitting: boolean;
  onClose: () => void;
  onSubmit: (values: VendorErrorCodeFormValues) => Promise<void>;
}

const MODE_ACCENT = {
  create: {
    color: 'var(--evimo-success)',
    bg: 'color-mix(in srgb, var(--evimo-success) 12%, transparent)',
    icon: <PlusCircleOutlined />,
  },
  edit: {
    color: 'var(--evimo-warning)',
    bg: 'color-mix(in srgb, var(--evimo-warning) 12%, transparent)',
    icon: <EditOutlined />,
  },
} as const;

export function VendorErrorCodeDrawer({
  open,
  mode,
  initialValues,
  manufacturerOptions,
  assetTypeOptions,
  submitting,
  onClose,
  onSubmit,
}: VendorErrorCodeDrawerProps) {
  const { t } = useTranslation('errorManagement');
  const [form] = Form.useForm<VendorErrorCodeFormValues>();
  const accent = MODE_ACCENT[mode];

  useEffect(() => {
    if (!open) {
      return;
    }
    if (mode === 'edit' && initialValues) {
      form.setFieldsValue(initialValues);
      return;
    }
    form.resetFields();
  }, [open, mode, initialValues, form]);

  return (
    <Drawer
      title={
        <ViewDrawerTitle
          title={
            mode === 'create' ? t('drawer.createTitle') : t('drawer.editTitle')
          }
          accentColor={accent.color}
          accentBg={accent.bg}
          icon={accent.icon}
        />
      }
      open={open}
      onClose={onClose}
      width={520}
      destroyOnClose
      styles={{
        header: {
          borderBottom: `2px solid ${accent.color}`,
        },
      }}
      extra={
        <Button
          type="primary"
          loading={submitting}
          onClick={() => form.submit()}
        >
          {mode === 'create'
            ? t('drawer.createSubmit')
            : t('drawer.editSubmit')}
        </Button>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        disabled={submitting}
      >
        <VendorErrorCodeRequiredFields
          manufacturerOptions={manufacturerOptions}
          assetTypeOptions={assetTypeOptions}
        />
        <VendorErrorCodeAdvancedFields />
      </Form>
    </Drawer>
  );
}
